import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HorizontalScrollList } from '@/components/HorizontalScrollList';
import { cn } from '@/lib/utils';
import type { LutCard } from '@/lib/video-effects/lutCards';

/** Shown while the thumbnail is not rendered yet, or when it can't be
 *  (no WebGL2, CORS-blocked frame). */
const SWATCHES: Record<string, string> = {
  original: 'linear-gradient(110deg,#2b2b2b,#7a7a7a,#d6d6d6)',
  'preset:warm': 'linear-gradient(110deg,#613629,#efad67,#fbe0af)',
  'preset:cool': 'linear-gradient(110deg,#243c59,#76b2c7,#d3e7e2)',
  'preset:cinema': 'linear-gradient(110deg,#163943,#689a93,#d89058)',
  'preset:vintage': 'linear-gradient(110deg,#605444,#b29a70,#d9cbb1)',
  'preset:vivid': 'linear-gradient(110deg,#852955,#de743c,#6aa975)',
  'preset:mono': 'linear-gradient(110deg,#202020,#888,#eee)',
  custom: 'linear-gradient(110deg,#3a2f52,#8a78b8,#e0d8f2)',
};

/** LUT picker styled like the editor's templates carousel: one card per look
 *  with the current clip frame rendered through it. */
export function LutCarousel({ cards, thumbnails, onSelect, disabled = false }: {
  cards: LutCard[];
  thumbnails: Record<string, string>;
  onSelect: (card: LutCard) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <HorizontalScrollList>
      {cards.map((card) => {
        const label = card.label ?? t(card.labelKey);
        const thumbnail = thumbnails[card.key];
        return (
          <button
            key={card.key}
            type="button"
            data-testid="lut-card"
            data-lut={card.key}
            aria-pressed={card.selected}
            title={label}
            disabled={disabled}
            onClick={() => onSelect(card)}
            className={cn(
              'relative w-24 shrink-0 overflow-hidden rounded-md border-2 bg-black text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              card.selected ? 'border-primary' : 'border-border hover:border-muted-foreground',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            {card.selected && (
              <span aria-hidden="true" className="absolute right-1 top-1 z-10 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Check className="h-2.5 w-2.5" />
              </span>
            )}
            {thumbnail ? (
              <img src={thumbnail} alt="" draggable={false} className="block aspect-video w-full object-cover" />
            ) : (
              <span data-testid="lut-card-swatch" aria-hidden="true" className="block aspect-video w-full" style={{ background: SWATCHES[card.key] ?? SWATCHES.custom }} />
            )}
            <span className={cn(
              'block truncate px-1 py-0.5 text-center text-[10px] font-medium text-white',
              card.selected ? 'bg-primary/20' : 'bg-black/60',
            )}>
              {label}
            </span>
          </button>
        );
      })}
    </HorizontalScrollList>
  );
}
